import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { FaTrash, FaSpinner, FaClock, FaEnvelope, FaEye } from 'react-icons/fa';

const RequestManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;

    const requestsQuery = query(
      collection(db, 'requests'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requestList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRequests(requestList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleSelectAll = () => {
    if (selectedRequests.length === requests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(requests.map(request => request.id));
    }
  };

  const handleSelect = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Seçili istekleri silmek istediğinizden emin misiniz?')) return;

    setDeleting(true);
    try {
      for (const requestId of selectedRequests) {
        // Önce mesajları sil
        const messagesQuery = query(collection(db, 'requests', requestId, 'messages'));
        const messagesSnapshot = await getDocs(messagesQuery);
        for (const doc of messagesSnapshot.docs) {
          await deleteDoc(doc.ref);
        }

        // Sonra isteği sil
        await deleteDoc(doc(db, 'requests', requestId));
      }
      setSelectedRequests([]);
    } catch (error) {
      console.error('Error deleting requests:', error);
      alert('İstekler silinirken bir hata oluştu');
    }
    setDeleting(false);
  };

  if (user?.role !== 'admin') {
    return <div className="p-6">Bu sayfaya erişim yetkiniz yok.</div>;
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <FaSpinner className="animate-spin text-3xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Özel İstek Yönetimi</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            {selectedRequests.length === requests.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
          </button>
          {selectedRequests.length > 0 && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2"
            >
              {deleting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Siliniyor...</span>
                </>
              ) : (
                <>
                  <FaTrash />
                  <span>Seçilenleri Sil ({selectedRequests.length})</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectedRequests.length === requests.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kullanıcı
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Durum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Son Mesaj
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tarih
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => (
              <tr
                key={request.id}
                className={`hover:bg-gray-50 ${
                  selectedRequests.includes(request.id) ? 'bg-blue-50' : ''
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedRequests.includes(request.id)}
                    onChange={() => handleSelect(request.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {request.userEmail}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    request.status === 'completed' ? 'bg-green-100 text-green-800' :
                    request.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status === 'completed' ? 'Tamamlandı' :
                     request.status === 'in-progress' ? 'İşleniyor' :
                     'Beklemede'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <FaEnvelope className="mr-2" />
                    <span className="truncate max-w-xs">
                      {request.lastMessage || 'Mesaj yok'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <FaClock className="mr-2" />
                    {request.createdAt?.toDate().toLocaleString('tr-TR')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Link
                    to={`/admin/requests/${request.id}`}
                    className="text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <FaEye className="mr-2" />
                    Görüntüle
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RequestManagement;